import signing from './letters-signing-en'

/** Decision letters (planning P4-2a, design §6): Settings → Planning → Decision letters. The case panel's strings join in Task 5. */
export default {
  letters: {
    signing,
    settings: {
      title: 'Decision letters',
      description:
        'Let officers make a decision letter from a case. The letterhead below is printed at the top of every letter, and the signatories are the people a letter may be signed by.',
      enabled: 'Offer decision letters on cases',
      enabledHint:
        'Off by default. Nothing is generated unless an officer asks for it after a decision. The letter templates supplied today are provisional: they have not been legally checked or confirmed by any council, and their PDFs carry the mark DRAF.',
      requireSignature: 'A letter must be signed before it is shared',
      requireSignatureOn:
        "Only a signed letter can be shared with the applicant. Without trust anchors every signed letter waits for an officer's explicit share.",
      requireSignatureOff:
        'A generated letter can be shared with the applicant as soon as it is issued, without a signature.',
      councilMs: 'Council name (Bahasa Melayu)',
      councilEn: 'Council name (English, optional)',
      councilEnHint: 'Used only beside an official English source. Letters are in Bahasa Melayu.',
      addressLines: 'Address',
      addressLinesHint: 'One line per row, at most five.',
      phone: 'Telephone',
      email: 'Email',
      website: 'Website',
      footer: 'Footer',
      logo: 'Council logo',
      logoHint: 'A PNG or JPEG of at most 256 KiB and 2048 pixels on each side. CMYK JPEGs are not accepted.',
      logoRemove: 'Remove the logo',
      logoType: 'Choose a PNG or JPEG file.',
      logoSize: 'The logo must be at most 256 KiB.',
      logoAlt: 'The council logo',
      signatories: 'Signatories',
      signatoriesHint: 'Up to ten people. The officer picks one when preparing a letter.',
      signatoryNone: 'No signatory yet.',
      signatoryName: 'Name',
      signatoryTitle: 'Title',
      signatoryAdd: 'Add a signatory',
      signatoryRemove: 'Remove',
      previewTitle: 'Letterhead preview',
      previewNote: 'This shows the letterhead only, as a letter would carry it. It is not a letter.',
      previewEmpty: 'Fill in the letterhead to see it here.',
      save: 'Save letter settings',
      saving: 'Saving…',
      saved: 'Letter settings saved.',
      problems: {
        tooLong: 'This is too long.',
        addressLinesTooMany: 'Use at most five address lines.',
        emailInvalid: 'Enter one email address.',
        websiteInvalid: 'Enter an address starting with http:// or https://.',
        signatoriesTooMany: 'Use at most ten signatories.',
        signatoryIncomplete: 'Each signatory needs a name and a title.',
      },
      trustAnchors: {
        title: 'Trust anchors',
        hint: "The certificate authorities this council trusts, as PEM, at most ten. A signed letter's certificate chain is checked against these and nothing else. Revocation is not checked yet, and a chain using a constraint gisify does not enforce reads as untrusted.",
        none: 'No trust anchors yet.',
        addFile: 'Add a certificate file',
        paste: 'Or paste a certificate',
        pasteAdd: 'Add the pasted certificate',
        remove: 'Remove {{name}}',
        unnamed: 'Certificate with no common name',
        unread: 'Not read yet. It is checked when you save.',
        fingerprint: 'SHA-256 fingerprint {{sha256}}',
        validUntil: 'Valid until {{date}}',
        expired: 'Expired on {{date}}. A signer that reaches only this anchor reads as untrusted.',
        notPem: 'That is not one PEM certificate. It must begin with BEGIN CERTIFICATE and end with END CERTIFICATE.',
        tooLarge: 'A certificate can be at most 16 KiB.',
        tooMany: 'Use at most ten trust anchors.',
        duplicate: 'That certificate is already listed.',
        readFailed: 'That file could not be read.',
      },
    },
    panel: {
      title: 'Decision letters',
      intro:
        'Make a decision letter from a decision on this case. A letter stays a draft until an officer issues it; issuing keeps it in the case.',
      offers: 'Letters you can prepare',
      noOffers: 'No letter is offered for this case yet.',
      offerFor: 'For the decision at step {{seq}}',
      prepare: 'Prepare',
      provisional:
        'Provisional template: not legally checked or confirmed by any council. The PDF carries the mark DRAF.',
      listTitle: 'Letters on this case',
      none: 'No letter has been prepared yet.',
      newest: 'Newest for this decision',
      forDecision: 'Decision at step {{seq}}',
      shared: 'Shared with the applicant',
      notShared: 'Not shared',
      openDraft: 'Open the draft',
      issue: 'Issue',
      discard: 'Discard',
      share: 'Share with the applicant',
      hide: 'Hide from the applicant',
      rendering: 'The letter is being made…',
      renderingSlow: 'This is taking longer than expected. Check again in a moment.',
      retry: 'Check again',
      failed: 'The letter could not be made ({{code}}).',
      failedGlyph:
        'The letter could not be made: {{field}} holds a character the letter fonts do not have. Correct it and prepare the letter again, or upload the letter as a document.',
      failedGlyphTemplate:
        'The letter could not be made: {{field}} holds a character the letter fonts do not have. Tell an administrator, and upload the letter as a document for now.',
      field: {
        applicantName: "the applicant's name",
        applicantFirm: "the applicant's firm",
        caseReference: 'the case reference',
        decisionNumber: 'the decision number',
        decisionDate: 'the decision date',
        councilName: 'the council name on the letterhead',
        letterheadAddress: 'the letterhead address',
        letterheadContact: 'the letterhead contact details',
        letterheadFooter: 'the letterhead footer',
        signatoryName: "the signatory's name",
        signatoryTitle: "the signatory's title",
        read: 'the decision value "{{alias}}"',
        blockLabel: 'the label of part {{n}} of the letter template',
        blockText: 'the wording of part {{n}} of the letter template',
        unknown: 'a part of the letter',
      },
      signatureRequired: 'Download the issued letter, sign it in your own tool, and upload it here.',
      downloadIssued: 'Download issued letter',
      downloadSigned: 'Download signed letter',
      uploadSigned: 'Upload signed letter',
      uploadSignedAgain: 'Upload signed letter again',
      uploading: 'Uploading the signed letter… {{percent}}%',
      uploadFinalizing: 'Checking the upload…',
      verifying: 'Checking the signature…',
      signedShared: 'Shared',
      signedSharedAuto: 'Shared automatically',
      issuedStillShown: 'The applicant still sees the issued letter. Sharing the signed letter replaces it.',
      ordinaryFallback: 'It can still be uploaded as an ordinary document, which links to nothing.',
      noOrdinaryFallback:
        'At this stage of the case it cannot be kept as a separate document. Sign the issued file again and upload that.',
      signed: {
        kind: { pades: 'Signature type: PAdES', not_pades: 'Signature type: a PDF signature that is not PAdES' },
        chain: {
          trusted: 'Certificate: chains to a CA configured for this council',
          untrusted: 'Certificate: does not chain to a configured CA',
          unchecked: 'Certificate: not checked against a CA',
        },
        signer: 'Signer: {{name}}',
        signerUnknown: 'Signer: not recorded',
        issuer: 'Issued by: {{name}}',
        serial: 'Serial number: {{serial}}',
        validity: 'Valid from {{from}} to {{to}}',
        fingerprint: 'SHA-256 fingerprint: {{sha256}}',
        signingTime: "Signing time, as the signer's own tool claims it: {{date}}",
        signatory: {
          match: "Signatory: the certificate's name matches the chosen signatory",
          mismatch: "Signatory: the certificate's name does not match the chosen signatory",
          not_comparable: 'Signatory: this certificate could not be compared with the chosen signatory',
        },
        stamp: 'Stamp: page {{page}}, {{percent}}% of the page',
        noStamp: 'No visible stamp',
        noRevocation: 'Whether this certificate has been revoked was not checked.',
        limit:
          'gisify checked that the issued page content was not rewritten; it did not render the file, and a signature stamp can cover text inside its box.',
      },
      warning: {
        chain_unchecked:
          'No trust anchors are configured, so this certificate was not checked against a CA. A self-signed certificate reaches this point.',
        chain_untrusted:
          'This certificate does not chain to a configured CA, or its chain could not be understood. That does not by itself mean the certificate is invalid.',
        signatory_mismatch:
          'The name on this certificate does not match the chosen signatory. Check that the right person signed.',
        signatory_not_comparable:
          'This certificate could not be compared with the chosen signatory. Check that the right person signed.',
        signer_ic_number:
          'This certificate holds what looks like an IC number. Sharing the letter discloses it to the applicant.',
        signature_stamp_large:
          "The signature's stamp covers {{percent}}% of page {{page}}. Open the signed letter and check that it covers nothing before sharing it.",
        decision_superseded: 'A newer decision replaced the one this letter was made from.',
        letter_superseded:
          'A newer letter of this decision has been issued. This copy is of the older letter, so its text may be out of date.',
      },
      refusal: {
        signed_letter_not_derived: 'This file is not the issued letter with a signature added.',
        signed_letter_overrides_content: 'This file changes more of the letter than a signature needs.',
        signed_letter_unsupported_structure:
          "The structure of this file could not be checked. If the signature's reason or stamp holds the word xref, startxref or trailer, sign the issued file again without it.",
        signature_invalid: 'The signature in this file does not verify. The file may have changed after it was signed.',
        signature_algorithm_refused:
          'This signature uses an algorithm or key size gisify does not accept. Sign the issued file again with another.',
        stage_held_externally: 'Another system holds this case now, so the signed letter was not accepted.',
        stage_locked: 'This case no longer accepts a signed letter.',
        too_large: 'This file is larger than the limit for a signed letter.',
        upload_missing: 'The uploaded file could not be found. Upload it again.',
        issued_letter_unreadable:
          'The issued letter could not be read to compare it with this file. Try again, and tell an administrator if it happens again.',
        verify_timeout: 'The check took too long and was stopped. Upload the file again.',
        dispatch_failed: 'The check could not be started. Upload the file again.',
        unknown: 'This file could not be accepted. Sign the issued file again and upload that.',
      },
      uploadRefused: {
        too_large: 'The signed letter was refused: the file is larger than the limit for a signed letter.',
        empty: 'The signed letter was refused: the file is empty.',
        unsupported_type: 'The signed letter was refused: the file is not a PDF.',
        missing: 'The signed letter was refused: the file did not reach the server. Upload it again.',
        size_mismatch:
          'The signed letter was refused: the file that arrived is not the size that was sent. Upload it again.',
        unreadable: 'The signed letter was refused: the file that arrived could not be read. Upload it again.',
        unknown: 'The signed letter was refused. Upload the file again.',
      },
      shareDialog: {
        title: 'Share the signed letter',
        intro:
          'The applicant will see the signed letter in place of the issued one. Read each warning before you share.',
        none: 'No warning applies to this signature.',
        confirmIc: "I confirm this letter discloses the signer's IC number to the applicant.",
        submit: 'Share',
        issued: {
          title: 'Share the issued letter',
          intro: 'The applicant will see this issued letter. Read each warning before you share.',
          none: 'No warning applies to this letter.',
        },
      },
      warnCommunicated: 'The communicated date on this decision is earlier than today. Sharing now does not change it.',
      loadFailed: 'The letters could not be loaded.',
      status: {
        rendering: 'Being made',
        draft: 'Draft',
        failed: 'Failed',
        issued: 'Issued',
        discarded: 'Discarded',
        expired: 'Expired',
      },
      held: {
        title: 'Decision emails waiting for a letter',
        body: 'The applicant is emailed about a decision once its letter is shared. You can send that email now without waiting.',
        item: 'Decision at step {{seq}}',
        release: 'Send the email now',
        released: 'The email will be sent shortly.',
      },
      dialog: {
        title: 'Prepare a decision letter',
        template: 'Letter: {{template}}',
        signatory: 'Signatory',
        pick: 'Choose a signatory',
        none: 'There is no signatory yet. An administrator adds one in Settings → Planning.',
        submit: 'Prepare the letter',
        cancel: 'Cancel',
        note: 'The letter is made from this decision and kept as a draft for you to open.',
      },
    },
  },
  errors: {
    letters_off: 'Decision letters are off. An administrator turns them on in Settings → Planning.',
    template_not_offered: 'That letter is not offered on this submission.',
    letterhead_incomplete: 'The letterhead is not complete. An administrator completes it in Settings → Planning.',
    letter_value_missing: 'This submission does not hold a value the letter needs.',
    letter_not_found: 'That letter was not found on this submission.',
    letter_not_draft: 'That letter is no longer a draft. Reload the case and try again.',
    letter_not_issued: 'That letter has not been issued.',
    letter_draft_stale:
      'A newer decision replaced the one this draft was made from, so this draft cannot be issued. If the letter is still listed under Letters you can prepare, prepare it again. If it is not listed, it can no longer be prepared on this submission.',
    letter_draft_changed: 'The stored draft is not the one that was rendered. Prepare the letter again.',
    signature_required: 'A letter must be signed before it is shared.',
    signatory_unknown: 'That signatory is not on the letterhead.',
    invalid_letter_request: 'That letter request is not valid.',
    held_email_not_found: 'No decision email is waiting for a letter on that step.',
    letter_imported: 'That letter came from another system, so it cannot be signed here.',
    letter_signature_verifying: 'A signed copy of that letter is still being checked. Wait for the result.',
    letter_already_signed: 'That letter already has a verified signed copy.',
    letter_not_verifying: 'That signed copy is no longer being checked. Reload the case.',
    signature_warning_unacknowledged: 'Each warning must be acknowledged before the letter is shared.',
  },
}
